import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Scene } from "@/scene/Scene";
import { BootSequence } from "@/ui/Loader/BootSequence";
import { Poster } from "@/ui/Loader/Poster";
import { Header } from "@/ui/HUD/Header";
import { Statusline } from "@/ui/HUD/Statusline";
import { InventoryPanel } from "@/ui/HUD/InventoryPanel";
import { CommandPalette } from "@/ui/HUD/CommandPalette";
import { useKeyboardCapture } from "@/hooks/useKeyboardCapture";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { MobileCarousel } from "@/ui/Mobile/Carousel";
import { WhoamiModal } from "@/ui/WhoamiModal";
import { useProgressStore } from "@/state/useProgressStore";
import { useSceneStore } from "@/state/useSceneStore";
import { MonitorRouter } from "@/ui/MonitorContent/Router";
import { FirstVisitTutorial } from "@/ui/HUD/FirstVisitTutorial";

const closeMonitor = (): void => {
  useSceneStore.getState().setActiveSection(null);
  useSceneStore.getState().setActiveMonitorRoute(null);
  if (typeof window !== "undefined") {
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
  }
};

const MonitorOverlay = (): JSX.Element | null => {
  const active = useSceneStore((s) => s.activeSection) === "monitor";
  if (!active) return null;
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-bg0/95 p-4 backdrop-blur-sm"
      onClick={closeMonitor}
      role="presentation"
    >
      <div
        className="h-full w-full max-w-[1100px]"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <MonitorRouter />
      </div>
    </div>
  );
};

const App = (): JSX.Element => {
  const [bootDone, setBootDone] = useState<boolean>(false);
  const [invOpen, setInvOpen] = useState<boolean>(false);
  const [whoamiOpen, setWhoamiOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width: 992px)");

  const onBootComplete = useCallback(() => setBootDone(true), []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("dev")) {
      useProgressStore.getState().unlock("konami");
    }
    const applyHash = (hash: string): void => {
      if (!hash || hash === "#/" || hash === "#") return;
      const stripped = hash.replace(/^#/, "");
      if (stripped === "" || stripped === "/" || stripped === "/about") return;
      const route = hash.startsWith("#/") ? hash : `#${hash}`;
      useSceneStore.getState().setActiveSection("monitor");
      useSceneStore.getState().setActiveMonitorRoute(route);
    };
    applyHash(window.location.hash);
  }, []);

  const keys = useMemo(
    () => ({
      i: () => setInvOpen((v) => !v),
      Escape: () => {
        setInvOpen(false);
        setWhoamiOpen(false);
        if (useSceneStore.getState().activeSection !== null) {
          closeMonitor();
          useSceneStore.getState().setFocusedObject(null);
        }
      },
    }),
    [],
  );
  useKeyboardCapture({
    keys,
    konami: () => useProgressStore.getState().unlock("konami"),
    whoami: () => setWhoamiOpen(true),
  });

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-bg0 text-ink0">
      {!bootDone && <Poster />}
      <Scene />
      <Header />
      <Statusline />
      <InventoryPanel open={invOpen} onClose={() => setInvOpen(false)} />
      <CommandPalette />
      {isMobile && <MobileCarousel />}
      {!bootDone && <BootSequence onComplete={onBootComplete} />}
      <WhoamiModal open={whoamiOpen} onClose={() => setWhoamiOpen(false)} />
      <MonitorOverlay />
      {!isMobile && <FirstVisitTutorial />}
    </div>
  );
};

export default App;
